import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { supabase } from '../../lib/db';
import { useAuth } from '../../components/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useHistory } from 'react-router-dom';

interface NotificationListProps {
  entityType?: string;
}

export interface NotificationListHandle {
  refresh: () => void;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  entity_type: string;
  deep_link: string;
  metadata: Record<string, any>;
  status: string;
}

export const NotificationList = forwardRef<NotificationListHandle, NotificationListProps>(({ entityType }, ref) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const history = useHistory();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotificationsInner = async () => {
    if (!user) return;
    try {
      setLoading(true);
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['delivered', 'read'])
        .order('created_at', { ascending: false });

      if (entityType === 'other') {
        query = query.not('entity_type', 'in', '("booking","chat")');
      } else if (entityType) {
        query = query.eq('entity_type', entityType);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: () => { fetchNotificationsInner(); }
  }));

  useEffect(() => {
    if (!user) return;

    fetchNotificationsInner();

    // Subscribe to changes
    const subscription = supabase
      .channel('notifications_list')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchNotificationsInner();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, entityType]);

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ status: 'read' })
        .eq('id', notification.id);

      // Notify badge counts to update immediately
      window.dispatchEvent(new CustomEvent('notifications-changed'));

      // Update the notification status in local state without removing it
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, status: 'read' }
            : n
        )
      );

      // Navigate via deep_link or fallback based on entity_type
      if (notification.deep_link) {
        history.push(notification.deep_link);
      } else if (notification.entity_type === 'booking') {
        history.push('/appointments');
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p>{t('noNotifications')}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          onClick={() => handleNotificationClick(notification)}
          className={`
            p-4 hover:bg-gray-50 cursor-pointer transition-colors
            ${notification.status === 'delivered' ? 'bg-blue-50' : ''}
          `}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="font-medium text-gray-900">{notification.title}</p>
              <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
              <p className="text-xs text-gray-400 mt-1">
                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
              </p>
            </div>
            {notification.status === 'delivered' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {t('new')}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
});